<template>
  <div data-cy="game-import-page" class="game-import-page">
    <section class="import-games-container">
      <ol class="steps-description">
        <li class="big-step">
          <span>
            Go to
            <a target="_blank" href="https://kitchen.voodoo-tech.io/">Kitchen app manager</a>
            and search for your game
          </span>
        </li>
        <li class="big-step">
          <span>Open "show more" in "game information" section and copy the App ID</span>
        </li>
        <li class="big-step">
          <span>Paste the App ID in the field below and validate</span>
        </li>
      </ol>

      <VdForm
        data-cy="import-games-form"
        :form="form"
        size="large"
        :loading="loading"
        class="import-games-form"
        @submit="onSubmit"
        @cancel="onCancel"
      >
        <VdFormField
          v-model="form.unifiedAppId"
          prop="unifiedAppId"
          type="text"
          data-cy="import-game-id"
          class="import-games-input"
          required
          placeholder="Unified App Id (UUID Format)"
          clearable
        />
      </VdForm>
    </section>
  </div>
</template>

<script lang="ts">
/**
 * This component allow to import a game from its UNified App ID (UAI).
 * It describes the steps to get the UAI from the Kitchen.
 * When the use enters the UAI and submit, the game is imported in VoodooTune
 * (ie: is accessible on the games list page)
 */
import { VdForm, VdFormField } from "@voodoo.io/voodoo-ui";
import { defineComponent, reactive } from "vue";
import { useRouter } from "vue-router";

import { queryNames, useImportGameMutation, useMutationResponse } from "@/graphql";
import links from "@/router/links";

export default defineComponent({
  name: "GameImportPage",

  components: {
    VdForm,
    VdFormField,
  },

  setup() {
    const router = useRouter();
    /**
     * setup the form which contains the unifiedAppId (UAI)
     */
    const form = reactive({
      unifiedAppId: "",
    });

    const { mutate, loading } = useImportGameMutation({ refetchQueries: [queryNames.GamesListPage] });

    /**
     * onSubmit will be called when the form is submitted.
     * If the form is valid, it will post the data to the server
     * and redirect to the games list.
     */

    const onSubmit = async () => {
      const response = await mutate({ unifiedAppId: form.unifiedAppId });
      const importResult = useMutationResponse(response, (resp) => resp?.importGame);
      if (importResult?.success) {
        router.push({ name: links.games.list });
      }
    };

    /**
     * onCancel, redirect to the games list page
     */
    const onCancel = () => {
      router.push({ name: links.games.list });
    };

    return {
      form,
      loading,
      onSubmit,
      onCancel,
    };
  },
});
</script>

<style lang="scss" scoped>
.game-import-page {
  width: 100%;
  height: 100%;

  .import-games-container {
    width: 370px;
    margin: 20% auto;
    transform: translateY(-50%);

    .steps-description {
      counter-reset: big-step-counter;
      list-style: none;
      padding-left: size("ML");

      > li {
        font-size: font("M");
        counter-increment: big-step-counter;
        color: var(--vd-color-grey);
        height: 60px;
        line-height: 60px;
        position: relative;
        margin-bottom: 26px;

        > span {
          display: inline-block;
          vertical-align: middle;
          line-height: normal;
          width: 100%;
          margin: auto;
        }

        &:before {
          content: counter(big-step-counter);
          color: $cloudy-blue;
          font-size: font("extra");
          font-weight: 800;
          position: absolute;
          z-index: $z-big-step-number;
          left: 0;
          top: 0;
          transform: translateX(-50%);
        }
      }
    }

    .import-games-form {
      .import-games-input {
        margin-bottom: 20px;
      }

      .actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }
  }
}
</style>
